import * as React from 'react';
import { useMemo } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { cores } from '../styles/styleUtils';

const COR_VAZAO    = '#1565C0';
const COR_CONSUMO  = '#F57C00';
const COR_ECONOMIA = '#2E7D32';

// ── Rótulo nas barras ─────────────────────────────────────────────────
const RotuloBarras = ({ x, y, width, height, value, fontSize = 10 }) => {
  if (!value || height < 14) return null;
  return (
    <text
      x={x + width / 2}
      y={y + height / 2 + fontSize / 3}
      fill="#fff"
      textAnchor="middle"
      fontSize={fontSize}
      fontWeight="bold"
    >
      {typeof value === 'number' ? value.toFixed(1) : value}
    </text>
  );
};

export default function PreviewProducaoBombeamento({ proposta }) {
  const meses = proposta?.dimensionamentoBomb?.meses || [];

  // ── Gráfico 1 — Vazão Média Mensal (m³/h) ───────────────────────
  const dadosVazao = useMemo(() =>
    meses.map((m) => ({
      mes:              m.mes.substring(0, 3),
      'Vazão (m³/h)':   m.vazaoHoraria,
    })),
  [meses]);

  // ── Gráfico 2 — Consumo × Economia ──────────────────────────────
  const dadosConsumo = useMemo(() =>
    meses.map((m) => ({
      mes:               m.mes.substring(0, 3),
      'Consumo (kWh)':   m.energiaMes,
      'Economia (R$)':   m.economiaMes,
    })),
  [meses]);

  // ── Totais ───────────────────────────────────────────────────────
  const vazaoMediaAnual  = meses.length > 0
    ? (meses.reduce((s, m) => s + (m.vazaoHoraria || 0), 0) / meses.length).toFixed(2)
    : '–';
  const consumoAnual     = meses.reduce((s, m) => s + (m.energiaMes || 0), 0).toFixed(0);
  const economiaAnual    = meses.reduce((s, m) => s + (m.economiaMes || 0), 0);

  if (!meses.length) {
    return (
      <Stack alignItems="center" justifyContent="center" flex={1}>
        <Typography color="error">
          Dados de dimensionamento não encontrados. Refaça o dimensionamento.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack flex={1} gap={3} px={2} py={1} sx={{ overflowY: 'auto' }}>

      {/* ── Resumo ── */}
      <Stack direction="row" gap={3} justifyContent="center" flexWrap="wrap">
        {[
          { label: 'Potência CC',        valor: `${proposta.dimensionamentoBomb?.potCC} kW` },
          { label: 'Potência Kit',       valor: `${proposta.kit?.potenciacc} kWp` },
          { label: 'HSP Mínimo',         valor: `${proposta.dimensionamentoBomb?.hspMin} h/dia` },
          { label: 'Vazão Média Anual',  valor: `${vazaoMediaAnual} m³/h` },
          { label: 'Consumo Anual',      valor: `${Number(consumoAnual).toLocaleString('pt-BR')} kWh` },
          { label: 'Economia Anual Est.',valor: economiaAnual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
        ].map((item) => (
          <Box
            key={item.label}
            sx={{
              backgroundColor: '#E3F2FD',
              border: '1px solid #90CAF9',
              borderRadius: 2,
              px: 3, py: 1.5,
              textAlign: 'center',
              minWidth: 130,
            }}
          >
            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            <Typography variant="subtitle1" fontWeight="bold" color="#1565C0">{item.valor}</Typography>
          </Box>
        ))}
      </Stack>

      {/* ── Gráfico 1 — Vazão Média Mensal ── */}
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" color="#1565C0" mb={1} textAlign="center">
          Projeção de Vazão Média Mensal (m³/h)
        </Typography>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={dadosVazao} margin={{ top: 16, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
            <XAxis dataKey="mes" tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1565C0' }} />
            <YAxis tick={{ fontSize: 11, fill: cores.textoClaro }} />
            <Tooltip formatter={(v) => [`${v.toFixed(2)} m³/h`, 'Vazão']} />
            <Legend wrapperStyle={{ color: cores.textoClaro }} />
            <Bar dataKey="Vazão (m³/h)" fill={COR_VAZAO} radius={[3,3,0,0]}>
              {dadosVazao.map((_, i) => <RotuloBarras key={i} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* ── Gráfico 2 — Consumo × Economia ── */}
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" color="#1565C0" mb={1} textAlign="center">
          Consumo Mensal × Economia Mensal
        </Typography>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={dadosConsumo} margin={{ top: 16, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
            <XAxis dataKey="mes" tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1565C0' }} />
            <YAxis tick={{ fontSize: 11, fill: cores.textoClaro }} />
            <Tooltip formatter={(v, name) =>
              name === 'Economia (R$)'
                ? [v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), name]
                : [`${v} kWh`, name]
            } />
            <Legend wrapperStyle={{ color: cores.textoClaro }} />
            <Bar dataKey="Consumo (kWh)" fill={COR_CONSUMO} radius={[3,3,0,0]}>
              {dadosConsumo.map((_, i) => <RotuloBarras key={i} />)}
            </Bar>
            <Bar dataKey="Economia (R$)" fill={COR_ECONOMIA} radius={[3,3,0,0]}>
              {dadosConsumo.map((_, i) => <RotuloBarras key={i} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Typography variant="caption" textAlign="center" sx={{ color: cores.textoClaro, opacity: 0.7 }}>
        * Estimativa baseada em dados solarimétricos e parâmetros do dimensionamento.
        Valores reais podem variar conforme condições locais, perdas na tubulação e sazonalidade.
      </Typography>

    </Stack>
  );
}